import { AlertTriangle, Check, Clock, MinusCircle } from 'lucide-react';

import { formatarData } from '@/nucleo/formato/datas';
import { cn } from '@/nucleo/utilitarios';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

import type { Credenciado } from './contrato';
import type { GatingDoCredenciado } from './detalheDados';

/**
 * O widget "Ciclo de Credenciamento" da barra lateral.
 *
 * NAO E UMA PORTAGEM do `ProgressTimeline` generico do protótipo — aquele
 * componente resolvia timeline de VARIAS entidades (termo, OS, recurso...) a
 * partir de "builders" plugaveis. Nenhuma outra tela deste lote precisa dessa
 * abstracao: so o credenciado tem este ciclo de quatro marcos. Portar o sistema
 * inteiro por um unico uso seria complexidade sem comprador — por isso este
 * componente e fixo nos quatro marcos, e nao um caso do generico.
 *
 * OS QUATRO MARCOS SAO SEMPRE OS MESMOS; O QUARTO MUDA DE ROTULO conforme a
 * situacao atual, porque "em operacao" so faz sentido para quem esta ATIVO —
 * quem esta suspenso, bloqueado ou descredenciado nao "opera pendente", ele
 * esta parado por outro motivo, e o rotulo diz qual.
 */

type Tom = 'completed' | 'current' | 'warning' | 'skipped';

const TOM: Record<Tom, { icon: typeof Check; cor: string; fundo: string }> = {
  completed: { icon: Check, cor: 'text-success', fundo: 'bg-success/15' },
  current: { icon: Clock, cor: 'text-primary', fundo: 'bg-primary/15' },
  warning: { icon: AlertTriangle, cor: 'text-warning', fundo: 'bg-warning/15' },
  skipped: { icon: MinusCircle, cor: 'text-muted-foreground', fundo: 'bg-muted' },
};

interface Etapa {
  label: string;
  tom: Tom;
  data?: string | null;
  descricao?: string | null;
  proximoPasso?: string;
}

interface CicloDeCredenciamentoProps {
  credenciado: Pick<
    Credenciado,
    | 'status'
    | 'inscricao_enviada_em'
    | 'pre_credenciado_em'
    | 'credenciado_em'
    | 'suspenso_em'
    | 'motivo_suspensao'
  >;
  gating: GatingDoCredenciado | undefined;
}

export function CicloDeCredenciamento({ credenciado, gating }: CicloDeCredenciamentoProps) {
  const { status } = credenciado;
  const ativo = status === 'ATIVO';
  const suspensoOuBloqueado = status === 'SUSPENSO' || status === 'BLOQUEADO';
  const inativo = status === 'INATIVO';

  const etapas: Etapa[] = [
    {
      label: 'Inscrição aceita',
      tom: 'completed',
      data: credenciado.inscricao_enviada_em,
    },
    {
      label: 'Pré-credenciado',
      tom: 'completed',
      data: credenciado.pre_credenciado_em,
    },
    {
      label: 'Credenciado ativo',
      tom: ativo ? 'current' : suspensoOuBloqueado ? 'warning' : 'completed',
      data: credenciado.credenciado_em,
      proximoPasso: ativo
        ? gating?.pode_operar === false
          ? 'Resolver bloqueios operacionais para liberar emissão de OS'
          : 'Vincular escolas e emitir ordens de serviço'
        : undefined,
    },
  ];

  if (suspensoOuBloqueado) {
    etapas.push({
      label: status === 'BLOQUEADO' ? 'Bloqueado' : 'Suspenso',
      tom: 'warning',
      data: credenciado.suspenso_em,
      descricao: credenciado.motivo_suspensao,
    });
  } else if (inativo) {
    etapas.push({ label: 'Descredenciado', tom: 'skipped' });
  } else {
    etapas.push({
      label: 'Em operação',
      tom: gating?.pode_operar === false ? 'warning' : ativo ? 'completed' : 'skipped',
      descricao: gating?.pode_operar === false ? gating.motivos[0] : undefined,
    });
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Ciclo de Credenciamento</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-0">
          {etapas.map((etapa, indice) => {
            const config = TOM[etapa.tom];
            const Icon = config.icon;
            const ultima = indice === etapas.length - 1;
            return (
              <li key={etapa.label} className="relative flex gap-3 pb-5 last:pb-0">
                {!ultima && (
                  <div
                    className="bg-border absolute left-[13px] top-7 bottom-0 w-px"
                    aria-hidden="true"
                  />
                )}
                <div
                  className={cn(
                    'relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                    config.fundo,
                  )}
                >
                  <Icon className={cn('h-3.5 w-3.5', config.cor)} aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      etapa.tom === 'skipped' && 'text-muted-foreground',
                    )}
                  >
                    {etapa.label}
                  </p>
                  {etapa.data && (
                    <p className="text-muted-foreground text-xs mt-0.5">
                      {formatarData(etapa.data)}
                    </p>
                  )}
                  {etapa.descricao && (
                    <p className="text-muted-foreground text-xs mt-1 italic">{etapa.descricao}</p>
                  )}
                  {etapa.proximoPasso && (
                    <p className="text-primary text-xs mt-1.5">
                      <span className="font-semibold">Próximo passo: </span>
                      {etapa.proximoPasso}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
